import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Lock, Loader2, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const formatErr = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (admin && admin !== false) {
    return <Navigate to="/admin/home" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate('/admin/home');
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Music className="text-[#00F0FF]" size={32} />
          <h1 className="text-2xl font-heading font-bold tracking-tight">DJ Store Admin</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#121214] border border-[#27272A] rounded-lg p-8 space-y-5" data-testid="login-form">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} className="text-[#00F0FF]" />
            <h2 className="text-lg font-heading font-semibold">Sign in</h2>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="bg-[#18181B] border-[#27272A]"
              placeholder="you@example.com"
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="bg-[#18181B] border-[#27272A]"
              data-testid="login-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-medium rounded-full h-11"
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : 'Sign in'}
          </Button>

          <p className="text-xs text-[#A1A1AA] text-center pt-2">
            Only the store owner can access this area.
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
